import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'

/**
 * *Save as vN* for a draft that needs a word before it becomes a version:
 * one that uploaded example files, or one whose tables or checks the builder
 * changed. For the first, the choice is whether the examples' rows are also
 * ingested into the config's database — on by default, since that is what
 * the examples were for, off when they were only ever samples. The second
 * only asks the person to confirm the contract change. A draft that would
 * refuse file types an intake takes says so too, without blocking the save.
 */
export function SaveDraftDialog({
  open,
  onOpenChange,
  configName,
  proposedVersion,
  askIngestExamples = true,
  contractWarning,
  intakeWarning,
  loading,
  error,
  onSubmit
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  configName: string
  proposedVersion: number
  askIngestExamples?: boolean
  contractWarning?: string
  intakeWarning?: string
  loading: boolean
  error?: string
  onSubmit: (choice: { ingestExamples: boolean }) => void
}) {
  const [ingestExamples, setIngestExamples] = React.useState(true)
  React.useEffect(() => {
    if (open) setIngestExamples(true)
  }, [open])
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as v{proposedVersion}?</DialogTitle>
          <DialogDescription>
            The draft becomes v{proposedVersion} of {configName}. Later uploads run against it.
          </DialogDescription>
        </DialogHeader>
        {contractWarning ? (
          <p
            role="note"
            className="rounded-md border border-st-warn/40 bg-st-warn-bg px-3 py-2 text-sm text-st-warn"
            data-testid="save-contract-warning"
          >
            {contractWarning}
          </p>
        ) : null}
        {intakeWarning ? (
          <p
            role="note"
            className="rounded-md border border-st-warn/40 bg-st-warn-bg px-3 py-2 text-sm text-st-warn"
            data-testid="save-intake-warning"
          >
            {intakeWarning}
          </p>
        ) : null}
        {askIngestExamples ? (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={ingestExamples}
              disabled={loading}
              data-testid="save-ingest-examples"
              onCheckedChange={(checked) => setIngestExamples(checked === true)}
            />
            <span className="flex flex-col gap-0.5">
              <span>Also ingest the example files</span>
              <span className="text-xs text-ink-3">
                Their rows land in <code className="font-mono">urn:db:{configName}</code> as one run
                under this build. Leave this off if the examples were only samples.
              </span>
            </span>
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-st-down">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={loading}
            data-testid="save-draft-confirm"
            onClick={() => onSubmit({ ingestExamples: !askIngestExamples || ingestExamples })}
          >
            {loading ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
            {loading
              ? 'Saving…'
              : !askIngestExamples
                ? `Save as v${proposedVersion}`
                : ingestExamples
                  ? `Save as v${proposedVersion} and ingest`
                  : `Save as v${proposedVersion} only`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
